import type { FlagKey } from '@gozoki/feature-flags'
import { Navigate } from '@tanstack/react-router'
import { useContext, type PropsWithChildren } from 'react'
import RbacAccessDeniedPage from '~/components/communication/RbacAccessDeniedPage'
import { useFeatureFlag, useFeatureFlagsReady } from '~/hooks/useFeatureFlag'
import { AuthContext } from '~/utils/context/auth.context'
import {
  usePermissionGuard,
  type LegacyRightsRequirement,
  type PermissionRequirement,
} from '~/utils/rbac'
import { featureGuardState, rightsGuardState } from './route-guard-states'

/**
 * Gardes de rendu des pages du back-office, en un seul module :
 * - `FeatureRouteGuard` : page derrière un flag Flipt ;
 * - `RightsRouteGuard` : droit v2, avec repli sur les droits hérités tant que `permission-v2` est
 *   OFF.
 * Les enfants ne sont montés (et leurs requêtes lancées) qu'une fois la garde passée.
 */

const Loading = () => <p className="p-4 text-sm text-muted-foreground">Chargement…</p>

/** Page derrière un flag : chargement tant que Flipt répond, flag OFF → retour à l'accueil. */
export function FeatureRouteGuard({ flag, children }: PropsWithChildren<{ flag: FlagKey }>) {
  const state = featureGuardState({
    flagsReady: useFeatureFlagsReady(),
    featureEnabled: useFeatureFlag(flag),
  })

  if (state === 'loading') return <Loading />
  if (state === 'redirect') return <Navigate to="/" replace />
  return <>{children}</>
}

/**
 * Page gardée par un droit. Tant que `permission-v2` est OFF, `withRoutePermissions` laisse tout
 * passer : les droits hérités (`legacyRights`, ceux qui cachent l'entrée de menu) restent la garde
 * effective, sans quoi un admin sans ce droit ouvrirait la page par URL et n'y verrait que des 403.
 */
export function RightsRouteGuard({
  permission,
  legacyRights,
  children,
}: PropsWithChildren<{
  permission: PermissionRequirement
  legacyRights: LegacyRightsRequirement
}>) {
  const { isLoading } = useContext(AuthContext)
  const { authorized, missingPermissions } = usePermissionGuard(permission, legacyRights)
  const state = rightsGuardState({ authLoading: isLoading, authorized })

  if (state === 'loading') return <Loading />
  if (state === 'denied') return <RbacAccessDeniedPage missingPermissions={missingPermissions} />
  return <>{children}</>
}
